import React from 'react';
import { Badge } from 'reactstrap';

// Platform JacksonConfig serializes booleans with an is-prefix (isFeatured, isNotVisible, ...).
export const flag = (row, name) =>
  row['is' + name.charAt(0).toUpperCase() + name.slice(1)] ?? row[name] ?? false;

const TILE_COLORS = ['#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626', '#0891b2', '#4f46e5'];

const tileColor = name => {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return TILE_COLORS[Math.abs(hash) % TILE_COLORS.length];
};

/**
 * The default logo: a rounded monogram tile derived from the name. It is also the advert form's
 * image fallback (UN-3248 AC4) — an advert with no uploaded image renders this rather than a broken
 * <img>, and the App Store preview shows exactly what the listing will show.
 */
export const AppTile = ({ name, imageUrl, size = 34 }) =>
  imageUrl ? (
    <img
      src={imageUrl}
      alt={name || ''}
      className="me-2 rounded flex-shrink-0"
      style={{ width: size, height: size, objectFit: 'cover' }}
    />
  ) : (
    <div
      className="d-flex align-items-center justify-content-center me-2 text-white fw-bold rounded flex-shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: tileColor(name),
        fontSize: Math.max(11, Math.round(size * 0.38))
      }}
    >
      {(name || '?').substring(0, 2).toUpperCase()}
    </div>
  );

/** Rounded monogram tile + name + uuid, per the prototype's APP column. */
/** Name cell of the listings: the icon (or the initials tile when the row has no image), name and a monospace subtitle. */
export const AppCell = ({ name, subtitle, imageUrl }) => (
  <div className="d-flex align-items-center">
    <AppTile name={name} imageUrl={imageUrl} />
    <div style={{ minWidth: 0 }}>
      <div className="fw-semibold">{name}</div>
      {subtitle ? (
        <div className="text-muted" style={{ fontSize: 11, fontFamily: 'monospace' }}>
          {subtitle}
        </div>
      ) : null}
    </div>
  </div>
);

/** Status + visibility badges for an advert row (Unlisted / Not Visible derive from `visibility`, CQ-3). */
export const AdvertBadges = ({ advert }) => (
  <span>
    {advert.status === 'PUBLISHED' ? (
      <Badge color="success" className="me-1">Published</Badge>
    ) : (
      <Badge color="secondary" className="me-1">Draft</Badge>
    )}
    {advert.visibility === 'UNLISTED' && <Badge color="dark" className="me-1">Unlisted</Badge>}
    {advert.visibility === 'HIDDEN' && <Badge color="danger" className="me-1">Not Visible</Badge>}
    {flag(advert, 'publicListed') && advert.visibility === 'LISTED' && (
      <Badge color="info" className="me-1">Public</Badge>
    )}
    {flag(advert, 'featured') && <Badge color="primary" className="me-1">Featured</Badge>}
    {flag(advert, 'mostPopular') && <Badge color="warning" className="me-1">Most Popular</Badge>}
    {flag(advert, 'preinstalled') && <Badge color="light" className="text-dark border me-1">Preinstalled</Badge>}
  </span>
);

const TYPE_COLORS = {
  INTEGRATION: 'info',
  BUILT_IN: 'success',
  EXTENSION: 'primary',
  MODULE: 'secondary'
};

/**
 * AppType -> what it is called on screen. A map, not string surgery: BUILT_IN (UN-3256) sentence-cases to
 * "Built_in", and a type this build has not met yet should still read as something rather than break the row.
 */
const TYPE_LABELS = {
  INTEGRATION: 'Integration',
  BUILT_IN: 'Built-in',
  EXTENSION: 'Extension',
  MODULE: 'Module'
};

// AppSource (app-store-messages) -> what the source module is called on the platform. Type alone
// (Integration / Module) cannot tell a carrier from a payment provider; the source can.
const SOURCE_LABELS = {
  PAYMENTS: 'Payments',
  SHIPPING: 'Shipping',
  NOTIFICATION: 'Notification',
  PIM: 'PIM',
  SETTINGS: 'Settings',
  EXTENSION_PLATFORM: 'Extensions'
};

export const sourceLabel = source => SOURCE_LABELS[source] || source || '';

export const typeLabel = type =>
  TYPE_LABELS[type] || (type ? (type.charAt(0) + type.slice(1).toLowerCase()).replace(/_/g, ' ') : '');

export const TypePill = ({ type }) =>
  type ? (
    <Badge pill color={TYPE_COLORS[type] || 'secondary'}>
      {typeLabel(type)}
    </Badge>
  ) : null;

/** Linked-module chip (name + link glyph), shown when the advert has a linked app. */
export const LinkedModuleChip = ({ name }) =>
  name ? (
    <Badge color="light" className="text-primary border">
      <i className="icon-link me-1" />
      {name}
    </Badge>
  ) : null;

/**
 * "Nothing matched" row, rendered INSIDE the table so the sortable headers stay put — a filtered-out
 * result is not the same thing as an empty area, and a bare header row reads as a broken page.
 */
export const NoResults = ({ colSpan, children }) => (
  <tr>
    <td colSpan={colSpan} className="text-center text-muted py-4">
      {children}
    </td>
  </tr>
);
